import React, { useRef, useEffect, useState } from 'react';
import Select, { components as Components } from 'react-select';
import { v4 as uuid } from 'uuid';
import { FixedSizeList as List } from 'react-window';

// Material ui
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Components
import Typography from '@components/UI-KIT/Typography';

// Icons
import { ChevronSmallDown } from '@/assets/icons';

// Styles
import VARIABLES from '@/assets/variables';
import './MultiSelectFilter.scss';

interface Props<T> {
    values: T[];
    onChange: (_values: T[]) => void;
    disabled?: boolean;
    label?: string;
    id?: string;
    options: SelectOption<T>[];
    checkbox?: boolean;
    dynamicRender?: boolean;
}

interface SelectOption<T> {
    label: string;
    value: T
}

const useStyles = makeStyles(() => ({
    checkbox: {
        '& span.ui-Checkbox-root': {
            padding: '0 !important',
            paddingLeft: '.5rem'
        }
    },
    selectLabel: {
        paddingRight: '1rem',
        whiteSpace: 'nowrap !important' as 'nowrap'
    },
    multiLabel: {
        borderBottom: `1px solid ${VARIABLES.palette.background_dark}`,
        padding: '4px 0 4px 8px', // positions text & border as the ValueContainer component
        lineHeight: '1rem !important'
    }
}));

function MultiSelectFilter<T>({
    values: valuesFromProps,
    onChange,
    disabled = false,
    label = '',
    options: optionsFromProps,
    id,
    checkbox = false,
    dynamicRender = false
}: Props<T>) {
    // match the selected values with the corresponding options
    const defaultValues = optionsFromProps.filter(o => valuesFromProps.includes(o.value));

    const [ valueContainerWidth, setValueContainerWidth ] = useState<number | 'auto'>('auto');

    const ValueContainerComponentRef = useRef<HTMLDivElement>();
    const InputContainerComponentRef = useRef<HTMLDivElement>();
    const ControlComponentRef = useRef<HTMLDivElement>();

    const ValueContainerComponentWidthController = (
        <div
            ref={ValueContainerComponentRef}
            style={{
                width: '100%',
                position: 'absolute',
                zIndex: -1000
            }}
            children={'\xa0'}
        />
    );

    const InputContainerSiblingComponent = (
        <div
            ref={InputContainerComponentRef}
        />
    );

    const ControlSiblingComponent = (
        <div
            ref={ControlComponentRef}
        />
    );

    const classes = useStyles();

    const handleInputFocus = () => {
        const input = InputContainerComponentRef.current.previousElementSibling.getElementsByTagName('input')[0];
        input.focus();
    };

    const handleMenuFocus = _e => {
        // const menu = e.target.querySelector('div[class*="menu"]');
        // console.log({ menu });

        // menu && menu.focus();
    };

    useEffect(() => {
        setValueContainerWidth(ValueContainerComponentRef.current.clientWidth);
        ValueContainerComponentRef.current.parentElement.addEventListener('click', handleInputFocus);
        dynamicRender && ControlComponentRef.current.parentElement.addEventListener('mouseover', handleMenuFocus);
        handleInputFocus();
        return () => {
            ValueContainerComponentRef.current.parentElement.removeEventListener('click', handleInputFocus);
            ControlComponentRef.current.parentElement.addEventListener('mouseenter', handleMenuFocus);
        };
    }, [ valuesFromProps ]);

    // useEffect(() => {
    //     if (!MenuListComponentRef.current) return;
    //     handleMenuFocus();
    // }, [ MenuListComponentRef.current ]);

    const handleSelect = (selectedOptions: SelectOption<T>[]) => {
        handleInputFocus();
        if (!selectedOptions) {
            onChange([]);
            return;
        }

        const vals = selectedOptions.map(o => o.value);
        onChange(vals);
    };

    const components = {
        Control: (props: any) => (
            <>
                {ControlSiblingComponent}
                <Components.Control
                    {...props}
                />
            </>
        ),
        Option: (props: any) => {
            const {
                data: {
                    label: checkboxLabel
                },
                isFocused
            } = props;

            return checkbox ? (
                <Components.Option
                    key={uuid()}
                    { ...props }
                >
                    <Grid
                        container
                        alignItems="center"
                    >
                        <Typography
                            color={isFocused ? 'secondary' : 'content'}
                        >
                            {checkboxLabel}
                        </Typography>
                    </Grid>
                </Components.Option>
            ) : (
                <Components.Option
                    key={uuid()}
                    { ...props }
                >
                    <Typography
                        color={isFocused ? 'secondary' : 'content'}
                    >
                        {checkboxLabel}
                    </Typography>
                </Components.Option>
            );
        },
        Input: (props: any) => (
            <>
                <Components.Input
                    {...props}
                />
                {InputContainerSiblingComponent}
            </>
        ),
        DropdownIndicator: () => (
            <ChevronSmallDown
                color={disabled ? VARIABLES.palette.content_light : VARIABLES.palette.secondary_color}
            />
        ),
        SingleValue: (props: any) => {
            const { data: _data, ...otherProps } = props;

            return (
                <Components.SingleValue
                    {...otherProps}
                />
            );
        },
        ValueContainer: ({ children, ...props }: any) => (
            <Components.ValueContainer
                {...props}
                children={[ ...children, ValueContainerComponentWidthController ]}
            />
        ),
        MenuList: ({ children, ...props }: any) => {
            const menuListRef = useRef<HTMLDivElement>();

            useEffect(() => {
                const element = menuListRef.current?.children[0].children[0].children[0].children[0].children[0].children[0] as HTMLElement;
                if (!element) return;
                element.addEventListener('click', e => e.stopPropagation());
                element.click();
                console.log(element);
            }, [ ]);

            return (
                <>
                    <Components.MenuList
                        {...props}
                    >
                        { (dynamicRender && !!optionsFromProps.length) ? (
                            <Grid
                                ref={menuListRef}
                            >
                                <List
                                    height={150}
                                    itemCount={children.length}
                                    itemSize={35}
                                    width={valueContainerWidth}
                                    itemData={children}
                                >
                                    {({ index, style }) => (
                                        <div
                                            style={{
                                                ...style,
                                                overflowX: 'hidden'
                                            }}
                                        >
                                            {children[index]}
                                        </div>
                                    )}
                                </List>
                            </Grid>
                        ) : children}
                    </Components.MenuList>
                </>
            );
        }
    };

    const styles = {
        container: provided => ({
            ...provided,
            width: 'inherit'
        }),
        control: provided => ({
            ...provided,
            borderRadius: '.5rem',
            backgroundColor: 'transparent',
            bottom: '.2rem',
            border: 'none',
            boxShadow: 'none',
            fontSize: '.875rem',
            minHeight: 'initial'
        }),
        valueContainer: provided => ({
            ...provided,
            borderBottom: `1px solid ${VARIABLES.palette.background_dark}`,
            padding: 0,
            paddingRight: valuesFromProps.length > 0 ? 0 : '.5rem',
            cursor: 'write',
            flexWrap: 'nowrap',
            width: valueContainerWidth,
            position: 'relative',
            transition: 'border-bottom 150ms ease-in',
            overflowX: 'scroll',
            '&:hover': {
                borderBottom: `1px solid ${VARIABLES.palette.secondary_light} !important`
            },
            '&:focus-within': {
                borderBottom: `1px solid ${VARIABLES.palette.secondary_color} !important`
            },
            '::-webkit-scrollbar': {
                height: '8px'
            },
            '::-webkit-scrollbar-thumb': {
                backgroundColor: VARIABLES.palette.background_dark,
                borderRadius: '100px'
            }
        }),
        multiValue: props => ({
            ...props,
            backgroundColor: VARIABLES.palette.background_dark,
            borderRadius: '16px',
            minWidth: 'auto'
        }),
        multiValueLabel: props => ({
            ...props,
            color: `${VARIABLES.palette.content_dark} !important`
        }),
        multiValueRemove: props => ({
            ...props,
            color: `${VARIABLES.palette.content_dark} !important`
        }),
        input: provided => ({
            ...provided,
            color: `${VARIABLES.palette.primary_dark} !important`,
            paddingTop: 0,
            paddingBottom: 0
        }),
        singleValue: provided => ({
            ...provided,
            color: `${VARIABLES.palette.primary_dark} !important`
        }),
        option: provided => ({
            ...provided,
            color: VARIABLES.palette.content_color,
            paddingLeft: '1rem',
            width: valueContainerWidth,
            cursor: 'pointer',
            backgroundColor: 'inherit',
            '&:first-of-type': {
                paddingTop: 0
            }
        }),
        noOptionsMessage: provided => ({
            ...provided,
            width: valueContainerWidth,
            padding: 0
        }),
        indicatorsContainer: provided => ({
            ...provided,
            padding: '0 .75rem',
            paddingRight: '0'
        }),
        indicatorSeparator: provided => ({
            ...provided,
            backgroundColor: 'inherit'
        }),
        menu: provided => ({
            ...provided,
            width: 'auto',
            backgroundColor: '#fafafa',
            borderRadius: ' 0 0 .5rem .5rem',
            padding: '.5rem 0',
            boxShadow: '0px 2px 4px 0px #00000016',
            fontSize: '.75rem',
            zIndex: '3001'
        }),
        menuList: provided => ({
            ...provided,
            overflowY: 'auto',
            overflowX: 'hidden'
        })
    };

    return (
        <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className="ui-Filters-select-contanier"
        >
            <Typography
                variant="input-head"
                color="primary-dark"
                className={classes.selectLabel}

            >
                {`${label}:`}
            </Typography>
            <Select
                isMulti
                // menuIsOpen // Debug & styling purposes only
                isClearable={false}
                classNamePrefix="ui-Filters-select"
                value={defaultValues}
                menuPlacement="auto"
                maxMenuHeight={dynamicRender ? undefined : 150}
                id={id}
                styles={styles}
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                placeholder=""
                noOptionsMessage={() => 'No hay más opciones para seleccionar'}
                components={components}
                options={optionsFromProps}
                onChange={handleSelect}
            />
        </Grid>
    );
};

export default MultiSelectFilter;
